import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { User, Stethoscope, Building2, Brain, Users, HeartHandshake, Mail, Phone, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const ROLES = [
  { id: "patient", label: "Patient / Member", short: "Patient", icon: User },
  { id: "doctor", label: "Doctor / Provider", short: "Doctor", icon: Stethoscope },
  { id: "therapist", label: "Therapist / Mental Health Pro", short: "Therapist", icon: Brain },
  { id: "family", label: "Family (Eldercare)", short: "Family", icon: Users },
  { id: "sathi", label: "Caregiver (Sathi)", short: "Sathi", icon: HeartHandshake },
  { id: "enterprise", label: "Enterprise / Hospital", short: "Enterprise", icon: Building2 },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginModal({ open, onClose, onSuccess }) {
  const { login } = useAuth();
  const [role, setRole] = useState("patient");
  const [channel, setChannel] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("identify"); // identify | otp
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timer <= 0) return;
    timerRef.current = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [timer]);

  const reset = () => {
    setStep("identify"); setCode(""); setIdentifier(""); setName(""); setTimer(0);
  };

  const handleClose = () => { reset(); onClose(); };

  const sendOtp = async () => {
    if (!identifier.trim()) { toast.error("Please enter your " + (channel === "email" ? "email" : "phone number")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { identifier, channel, role });
      setStep("otp");
      setTimer(30);
      if (data.mock && data.dev_otp) {
        toast("Demo code: " + data.dev_otp, { description: data.message || "Using demo mode — enter the code shown." });
      } else {
        toast.success(`Code sent to your ${channel === "email" ? "email" : "phone"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { identifier, channel, role, code, name });
      login(data.token, data.user);
      toast.success("Welcome to Candle Healthcare");
      reset();
      onSuccess?.(data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent data-testid="login-modal" className="p-0 overflow-hidden border-0 max-w-md gap-0 rounded-3xl shadow-2xl">
        <div className="bg-[#041E26] px-8 pt-8 pb-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F59E0B]/20 blur-3xl" aria-hidden="true" />
          <div className="flex items-center gap-2 relative">
            <Logo className="h-10" onDark showTagline />
          </div>
          <p className="text-white/50 text-sm mt-3 relative">Unified Portal Access</p>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {step === "identify" ? (
              <motion.div key="identify" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
                <button
                  data-testid="google-signin-btn"
                  type="button"
                  onClick={startGoogleLogin}
                  className="w-full mb-5 h-12 rounded-full border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center gap-3 text-[#041E26] font-semibold text-sm shadow-sm transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                  </svg>
                  Continue with Google
                </button>
                <div className="relative mb-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">or sign in with role &amp; OTP</span></div>
                </div>
                <Tabs value={role} onValueChange={setRole} className="w-full">
                  <TabsList className="grid grid-cols-3 w-full h-auto bg-slate-100 p-1 rounded-xl gap-1">
                    {ROLES.map((r) => (
                      <TabsTrigger key={r.id} value={r.id} data-testid={`role-tab-${r.id}`}
                        className="flex items-center justify-center gap-1.5 py-2.5 data-[state=active]:bg-white data-[state=active]:text-[#041E26] text-[11px] font-semibold rounded-lg">
                        <r.icon className="w-3.5 h-3.5" />
                        {r.short}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {ROLES.map((r) => (
                    <TabsContent key={r.id} value={r.id} className="mt-6">
                      <p className="text-sm text-slate-500 mb-4">{r.label} sign in via secure OTP.</p>
                    </TabsContent>
                  ))}
                </Tabs>

                <div className="flex gap-2 mb-5 bg-slate-100 p-1 rounded-xl">
                  <button data-testid="channel-email" onClick={() => setChannel("email")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${channel === "email" ? "bg-white text-[#041E26] shadow-sm" : "text-slate-500"}`}>
                    <Mail className="w-4 h-4" /> Email
                  </button>
                  <button data-testid="channel-sms" onClick={() => setChannel("sms")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${channel === "sms" ? "bg-white text-[#041E26] shadow-sm" : "text-slate-500"}`}>
                    <Phone className="w-4 h-4" /> Mobile
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">{channel === "email" ? "Email Address" : "Mobile Number"}</Label>
                    <Input data-testid="login-identifier-input" value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder={channel === "email" ? "you@example.com" : "+91 98765 43210"}
                      className="mt-1.5 h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Full Name (optional)</Label>
                    <Input data-testid="login-name-input" value={name}
                      onChange={(e) => setName(e.target.value)} placeholder="Your name"
                      className="mt-1.5 h-12 rounded-xl" />
                  </div>
                  <Button data-testid="send-otp-btn" onClick={sendOtp} disabled={loading}
                    className="w-full h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold text-base">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Verification Code"}
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div key="otp" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}>
                <button data-testid="otp-back-btn" onClick={() => setStep("identify")} className="flex items-center gap-1 text-sm text-slate-500 mb-5 hover:text-[#041E26]">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h3 className="font-head text-2xl text-[#041E26] font-semibold">Enter code</h3>
                <p className="text-sm text-slate-500 mt-1 mb-6">We sent a 6-digit code to <span className="font-semibold text-[#041E26]">{identifier}</span></p>
                <div className="flex justify-center" data-testid="otp-input">
                  <InputOTP maxLength={6} value={code} onChange={setCode}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-14 text-lg rounded-xl border-slate-300" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button data-testid="verify-otp-btn" onClick={verifyOtp} disabled={loading || code.length !== 6}
                  className="w-full h-12 mt-7 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white font-bold text-base">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
                </Button>
                <div className="text-center mt-4">
                  {timer > 0 ? (
                    <p className="text-sm text-slate-400">Resend code in {timer}s</p>
                  ) : (
                    <button data-testid="resend-otp-btn" onClick={sendOtp} className="text-sm font-semibold text-[#D97706] hover:underline">Resend code</button>
                  )}
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-5 text-xs text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5" /> End-to-end encrypted &bull; ABDM compliant
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
